import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { MapPin, Phone } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import { groupOpeningHours } from '@/lib/schedule'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { NAV_LINKS, business } from '@/constants/business'
import { siteConfig } from '@/constants/site'

export const SiteFooter: FC = (): ReactNode => {
    const hours = groupOpeningHours()

    return (
        <footer className='bg-ink text-white'>
            <Container className='grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr] lg:py-20'>
                <div>
                    <Logo inverted />

                    <p className='mt-5 max-w-xs text-sm leading-relaxed text-white/65'>
                        Snow foam, careful hands and a finish you'll want to show off. Drive in any time we're open, or book your slot online.
                    </p>

                    <Link href='/book' className={buttonStyles({ className: 'mt-6' })}>
                        Book a wash
                    </Link>
                </div>

                <div>
                    <h2 className='font-heading text-lg font-bold uppercase tracking-wider'>
                        Explore
                    </h2>

                    <ul className='mt-4 space-y-2.5 text-sm'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-white/70 transition-colors hover:text-white'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}

                        <li>
                            <Link href='/book' className='text-white/70 transition-colors hover:text-white'>
                                Book online
                            </Link>
                        </li>
                    </ul>
                </div>

                <div>
                    <h2 className='font-heading text-lg font-bold uppercase tracking-wider'>
                        Visit
                    </h2>

                    <address className='mt-4 text-sm not-italic leading-relaxed text-white/70'>
                        {business.address.map(line => (
                            <span key={line} className='block'>
                                {line}
                            </span>
                        ))}
                    </address>

                    <a
                        href={business.phone.href}
                        className='mt-4 inline-flex items-center gap-2 text-sm font-semibold text-aqua transition-colors hover:text-white'
                    >
                        <Phone className='size-4' />
                        {business.phone.display}
                    </a>

                    <a
                        href={business.directionsUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='mt-2 flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-white'
                    >
                        <MapPin className='size-4' />
                        Get directions
                    </a>
                </div>

                <div>
                    <h2 className='font-heading text-lg font-bold uppercase tracking-wider'>
                        Opening hours
                    </h2>

                    <dl className='mt-4 space-y-2 text-sm'>
                        {hours.map(group => (
                            <div key={group.days} className='flex justify-between gap-4'>
                                <dt className='text-white/70'>
                                    {group.days}
                                </dt>

                                <dd className='font-semibold tabular-nums'>
                                    {group.hours}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </Container>

            <div className='border-t border-white/10'>
                <Container className='flex flex-col gap-2 py-6 text-xs text-white/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        {business.tagline}
                    </p>
                </Container>
            </div>
        </footer>
    )
}
